<template>
  <vue-scroll class="page-calendar">
    <div
      class="page-header header-accent-gradient card-base card-shadow--small"
    >
      <el-row>
        <h1>Calendar</h1>
      </el-row>
      <el-breadcrumb separator="/">
        <el-breadcrumb-item :to="{ path: '/' }">
          <i class="mdi mdi-home-outline"></i>
        </el-breadcrumb-item>
        <el-breadcrumb-item>Calendar</el-breadcrumb-item>
      </el-breadcrumb>
    </div>

    <div v-loading="calendarLoading">
      <el-alert
        v-if="rulesForShowingAlert"
        style="margin-bottom: 10px"
        title="No tiene permiso para editar este calendario, no podrá guardar cambios"
        type="warning"
        center
        show-icon
      >
      </el-alert>

      <el-row>
        <!-- Calendar Title -->
        <el-col :span="11" :offset="4" v-if="currentChosenCalendarID > 0">
          <h2 style="margin: auto; text-align: center">
            {{ calendarTitle }}
          </h2>
        </el-col>
        <el-col v-else :span="15"> <p></p> </el-col>

        <!-- Employee Dropdown -->
        <el-col v-if="employees.length > 1" :span="3" :offset="1">
          <div class="flex justify-space-between mb-5">
            <div class="box">
              <el-select
                v-model="employeeId"
                placeholder="Responsable"
                size="mini"
                filterable
                clearable
              >
                <el-option
                  v-for="item in employees"
                  :key="item.value"
                  :label="item.label"
                  :value="item.value"
                >
                </el-option>
              </el-select>
            </div>
          </div>
        </el-col>
        <!-- Keeps layout consistent if employee dropdown isn't present -->
        <el-col v-else :span="4"> <p></p> </el-col>

        <!-- Calendar Dropdown -->
        <el-col :span="3">
          <div class="flex justify-space-between mb-5">
            <div class="box">
              <el-select
                v-model="currentChosenCalendarID"
                placeholder="Calendario"
                size="mini"
                filterable
                clearable
              >
                <el-option
                  v-for="item in listOfCalendarsForUser"
                  :key="item.ID"
                  :label="item.Title"
                  :value="item.ID"
                >
                </el-option>
              </el-select>
            </div>
          </div>
        </el-col>

        <!-- Add calendar button -->
        <el-col :span="1">
          <div class="flex justify-center">
            <el-button
              size="mini"
              plain
              @click="openCalendarDialog"
              :disabled="rulesForDisablingCreateCalendar"
            >
              ✚</el-button
            >
          </div>
        </el-col>

        <!-- Delete calendar button -->
        <el-col :span="1">
          <div>
            <el-button
              class="box"
              type="danger"
              size="mini"
              plain
              @click="openCalendarDeleteDialog"
              :disabled="rulesForDisablingSaveButton"
              v-if="currentChosenCalendarID > 0"
              ><i class="el-icon-delete"></i
            ></el-button>
          </div>
        </el-col>
      </el-row>

      <!-- Calendar -->
      <el-row>
        <el-col :span="24" v-if="currentChosenCalendarID > 0">
          <FullCalendar
            ref="fullCalendar"
            :options="config"
            class="calendar-wrap card-base card-shadow--medium"
          />
        </el-col>
        <el-col :span="24" v-else class="text-center">
          <h1>Debe seleccionar un calendario</h1>
        </el-col>
      </el-row>
    </div>

    <!-- Event Create Dialog -->
    <CalendarEventForm
      ref="form"
      @close-event-dialog="closeEventDialog"
      :form="form"
      :submitMode="submitMode"
      :dialogFormVisible="dialogFormVisible"
      :rulesForDisablingSaveButton="rulesForDisablingSaveButton"
      :rulesForDisablingDelete="rulesForDisablingDelete"
      :eventSelectedIsTask="eventSelectedIsTask"
      :userIsSupervisor="userIsSupervisor"
      :userId="userId"
      :currentlyChosenAgenda="currentlyChosenAgenda"
      :currentChosenCalendarID="currentChosenCalendarID"
    />

    <!-- Calendar Create Dialog -->
    <el-dialog title="Crear Calendario" :visible.sync="calendarFormVisible">
      <el-form :model="calendarForm" ref="calendarForm" label-position="top">
        <el-form-item
          label="Titulo"
          prop="title"
          :rules="[
            {
              required: true,
              message: 'El titulo es requerido',
              trigger: 'blur'
            }
          ]"
        >
          <el-input
            v-model="calendarForm.title"
            :disabled="eventSelectedIsTask"
          ></el-input>
        </el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button @click="closeCalendarDialog" v-loading="loading"
          >Cancel</el-button
        >
        <el-button type="primary" @click="submitCalendar" v-loading="loading"
          >Save</el-button
        >
      </span>
    </el-dialog>

    <!-- Calendar Delete Dialog -->
    <el-dialog
      title="Confirmación de eliminación"
      :visible.sync="calendarDeleteDialogVisible"
      width="30%"
    >
      <span>¿Está seguro/a de que desea eliminar este calendario?</span>
      <br />
      <span>Esta acción no puede ser revertida</span>
      <span slot="footer" class="dialog-footer">
        <el-button @click="closeCalendarDeleteDialog" v-loading="loading"
          >Cancelar</el-button
        >
        <el-button
          type="danger"
          @click="handleDeleteCalendar"
          v-loading="loading"
          >Eliminar</el-button
        >
      </span>
    </el-dialog>

    <!-- Calendar Share Drawer -->
    <ShareCalendarDrawer
      :openDrawer="openDrawer"
      :calendarID="currentChosenCalendarID"
      @close-drawer="handleCloseDrawer"
    />
  </vue-scroll>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import FullCalendar from "@fullcalendar/vue";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import listPlugin from "@fullcalendar/list";
import interactionPlugin from "@fullcalendar/interaction";
import allLocales from "@fullcalendar/core/locales-all";
import momentPlugin from "@fullcalendar/moment";

import { formatArrayToElSelect } from "@/utils";
import ShareCalendarDrawer from "@/components/calendar/ShareCalendarDrawer";
import CalendarEventForm from "@/components/calendar/CalendarEventForm.vue";

export default {
  name: "Calendar",
  components: {
    FullCalendar,
    CalendarEventForm,
    ShareCalendarDrawer
  },
  data() {
    return {
      employees: [],
      employeeId: 0,
      userId: 0,
      currentChosenCalendarID: undefined,

      loading: false,
      calendarApi: null,
      dialogFormVisible: false,
      submitMode: "", // Will be "Add" or "Edit"
      currentlyChosenAgenda: {},
      form: {
        title: "",
        description: "",
        allDay: false,
        startDate: "",
        startTime: "",
        endDate: "",
        endTime: "",
        agendaType: 0,
        color: "#4a596a"
      },
      submitModeOptions: {
        Add: "Agregar",
        Edit: "Editar"
      },
      predefineColors: [
        "#ff4500",
        "#ff8c00",
        "#ffd700",
        "#90ee90",
        "#00ced1",
        "#1e90ff",
        "#4a596a",
        "#c71585"
      ],

      calendarFormVisible: false,
      calendarForm: {
        title: ""
      },
      calendarDeleteDialogVisible: false,
      openDrawer: false
    };
  },
  async created() {
    this.employeeId = this.currentUser.EmployeeId;
    this.userId = this.currentUser.UserID;

    await this.GetPersonalCalendario(this.employeeId); // Creates personal calendar if it doesn't exist
    await this.getAllCalendarsForEmployee(this.employeeId);
    this.setPersonalCalendarAsCurrentlyChosen();

    await this.loadEmployees();
  },
  computed: {
    ...mapGetters("calendar", [
      "events",
      "weekendsVisible",
      "eventsAreLoading",
      "tasksAreLoading"
    ]),

    // Calendar configuration
    config() {
      return {
        ...this.configOptions,
        ...this.eventHandlers
      };
    },
    // Rule to control the Create Calendar button disabled state
    rulesForDisablingCreateCalendar() {
      // If supervising subordinates, allow creating calendars for them
      if (this.watchingSubordinatesCalendars) {
        return !this.userIsSupervisor; // only supervisors can create for employees
      }
      // Otherwise, for the current user, allow creating a calendar
      return false;
    },

    configOptions() {
      return {
        plugins: [
          dayGridPlugin,
          timeGridPlugin,
          listPlugin,
          interactionPlugin,
          momentPlugin
        ],
        initialView: "dayGridMonth",
        locales: allLocales,
        locale: "es", // the initial locale
        height: "auto",
        firstDay: 1,
        allDaySlot: true,
        slotEventOverlap: true,
        selectable: true,
        selectMirror: true,
        eventTimeFormat: {
          hour: "2-digit", //2-digit, numeric
          minute: "2-digit", //2-digit, numeric
          // second: "2-digit", //2-digit, numeric
          meridiem: true, //lowercase, short, narrow, false (display of AM/PM)
          hour12: false //true, false
        },
        navLinks: true, // can click day/week names to navigate views
        editable: true,
        durationEditable: false,
        dayMaxEventRows: true, // for all non-TimeGrid views # v5
        views: {
          month: {
            dayMaxEventRows: 5
          }
        },
        customButtons: {
          addEvent: {
            text: "Agregar Evento",
            click: this.addEventDialog
          },
          shareCalendar: {
            text: "Compartir",
            click: this.openShareDrawer
          }
        },
        headerToolbar: {
          left: "prev,next today",
          center: "title",
          right:
            "dayGridMonth,timeGridWeek,timeGridDay,listWeek addEvent shareCalendar"
        },
        weekends: this.weekendsVisible,
        events: this.$store.getters["calendar/events"]
      };
    },
    eventHandlers() {
      return {
        dateClick: this.onDateClick,
        eventClick: this.onEventClick,
        select: this.onDateSelect,
        eventDrop: this.onEventDrop
      };
    },
    calendarTitle() {
      if (this.currentChosenCalendarID) {
        let calendar = this.listOfCalendarsForUser.find(
          (element) => element.ID == this.currentChosenCalendarID
        );
        return calendar ? calendar.Title : "-";
      } else {
        return "-";
      }
    },

    listOfCalendarsForUser() {
      return this.$store.getters["calendar/calendarsForUser"];
    },
    calendarLoading() {
      return this.tasksAreLoading || this.eventsAreLoading;
    },

    // Settings
    watchingSubordinatesCalendars() {
      // This is when the Employee dropdown is being used
      return this.employeeId != this.currentUser.EmployeeId;
    },
    userIsSupervisor() {
      return this.employees.length > 1; // Explanation: Only supervisor roles get the dropdown, if the dropdown exists then the employees property will have contents
    },
    calendarIsPersonal() {
      let listOfCalendars = this.listOfCalendarsForUser;
      return listOfCalendars.find(
        (cal) => cal.ID === this.currentChosenCalendarID && cal.isPersonal
      );
    },
    userCanEditCalendar() {
      let listOfCalendars = this.listOfCalendarsForUser;
      return listOfCalendars.find(
        (cal) => cal.ID === this.currentChosenCalendarID && cal.canEdit
      );
    },
    eventSelectedIsTask() {
      return this.currentlyChosenAgenda.event
        ? this.currentlyChosenAgenda.event.extendedProps.isTask
        : false;
    },

    // Rules and permissions
    rulesForShowingAlert() {
      if (this.currentChosenCalendarID > 0 && this.listOfCalendarsForUser) {
        // Only show the alert if the user cannot edit the calendar and is not a supervisor
        if (!this.userCanEditCalendar && !this.userIsSupervisor) {
          return true;
        }
      }

      return false;
    },
    rulesForDisablingSaveButton() {
      // Can't add or edit Agendas to a calendar that belongs to subordinates unless the user is a supervisor
      if (this.watchingSubordinatesCalendars) {
        // If user is supervisor, allow creating/editing events (non-tasks) on subordinate calendars
        if (!this.userIsSupervisor) {
          // Non-supervisors cannot edit subordinate calendars
          if (!this.eventSelectedIsTask) {
            return true;
          }
        }
      }
      // Non-supervisors can't edit tasks
      if (this.eventSelectedIsTask && !this.userIsSupervisor) {
        return true;
      }
      // Can't edit if user doesn't have edit permission
      if (!this.userCanEditCalendar) {
        return true;
      }

      return false;
    },
    rulesForDisablingDelete() {
      return (
        this.watchingSubordinatesCalendars || // Can't delete elements from the calendar of a subordinate
        this.eventSelectedIsTask || // Can't delete tasks
        this.submitMode === this.submitModeOptions.Add || // Can't delete while adding
        !this.userCanEditCalendar // Can't delete without editing permission
      );
    }
  },
  methods: {
    ...mapActions("calendar", [
      "create",
      "update",
      "set_weekends_visible",
      "GetPersonalCalendario",
      "initTasks",
      "initTasksGantt",
      "updateTaskInApi",
      "delete",
      "getAllCalendarsForEmployee",
      "GetCalendario",
      "CreateCalendario",
      "DeleteCalendario"
    ]),

    findMyPersonalCalendar() {
      // Gets the personal calendar of the current user
      let listOfCalendars = this.listOfCalendarsForUser;
      let personalCalendar = listOfCalendars.find(
        (cal) => cal.isPersonal && cal.CreatedBy == this.employeeId
      );
      if (personalCalendar) {
        return personalCalendar.ID;
      } else {
        this.$message.error("Error al encontrar calendario personal");
      }
    },
    setPersonalCalendarAsCurrentlyChosen() {
      this.currentChosenCalendarID = this.findMyPersonalCalendar();
    },

    closeEventDialog() {
      // this.$refs.form.resetFields();
      this.dialogFormVisible = false;
      this.form = {
        title: "",
        description: "",
        allDay: false,
        startDate: "",
        startTime: "",
        endDate: "",
        endTime: "",
        color: "#4a596a",
        agendaType: 0
      };

      this.GetCalendario(this.currentChosenCalendarID);
      if (this.calendarIsPersonal) {
        // If the calendar is personal, get the Tasks of the owner of that calendar
        let calendarCreator = this.listOfCalendarsForUser.find(
          (cal) => cal.ID == this.currentChosenCalendarID
        ).CreatedBy;
        // this.initTasks(calendarCreator);
        this.initTasksGantt(calendarCreator);
      }
    },

    addEventDialog() {
      if (this.userCanEditCalendar || this.userIsSupervisor) {
        this.submitMode = this.submitModeOptions.Add;
        this.currentlyChosenAgenda = {};
        this.dialogFormVisible = true;
      }
    },
    onDateClick(arg) {
      if (this.userCanEditCalendar || this.userIsSupervisor) {
        this.currentlyChosenAgenda = arg;
        this.form.startDate = arg.date;
        this.form.endDate = arg.date;
        this.submitMode = this.submitModeOptions.Add;
        this.dialogFormVisible = true;
      }
    },
    onEventClick(arg) {
      this.currentlyChosenAgenda = arg;

      this.form = {
        title: arg.event.title,
        description: arg.event.extendedProps.description,
        allDay: arg.event.allDay,
        startDate: arg.event.extendedProps.startDate,
        startTime: arg.event.extendedProps.startDate,
        endDate: arg.event.extendedProps.endDate, // "end" was being returned as null so a new endDate prop is being used
        endTime: arg.event.extendedProps.endDate,
        color: arg.event.backgroundColor || "#4a596a",
        agendaType: arg.event.extendedProps.agendaType
      };
      this.submitMode = this.submitModeOptions.Edit;
      this.dialogFormVisible = true;
    },
    onEventDrop(arg) {
      this.currentlyChosenAgenda = arg;

      this.form = {
        title: arg.event.title,
        description: arg.event.extendedProps.description,
        allDay: arg.event.allDay,
        startDate: arg.event.extendedProps.startDate,
        startTime: arg.event.extendedProps.startDate,
        endDate: arg.event.start,
        endTime: arg.event.start,
        color: arg.event.backgroundColor || "#4a596a",
        agendaType: arg.event.extendedProps.agendaType
      };
      this.submitMode = this.submitModeOptions.Edit;
      this.dialogFormVisible = true;
    },
    onDateSelect(arg) {
      if (this.userCanEditCalendar || this.userIsSupervisor) {
        this.currentlyChosenAgenda = arg;

        this.form.startDate = arg.start;
        this.form.endDate = arg.end;

        this.submitMode = this.submitModeOptions.Add;
        this.dialogFormVisible = true;
      }
    },

    // For the Super User Employee Dropdown
    formatArrayToElSelect,
    async loadEmployees() {
      let params = {
        Employeeid: this.currentUser.EmployeeId,
        CompanySelected: this.currentCompany
      };
      const { data } = await this.$http.get(
        "/api/CMI/Objetives/GetUsersObjetivos",
        {
          timeout: 3 * 60 * 1000,
          params
        }
      );
      this.employees = this.formatArrayToElSelect(data, {
        value: "EmployeeID",
        label: "Name"
      });
    },
    openCalendarDialog() {
      this.calendarFormVisible = true;
    },
    closeCalendarDialog() {
      this.calendarFormVisible = false;
      this.calendarForm.title = "";
      this.GetPersonalCalendario(this.employeeId);
      this.getAllCalendarsForEmployee(this.employeeId);
      this.setPersonalCalendarAsCurrentlyChosen();
    },
    async submitCalendar() {
      const targetEmployeeId = this.employeeId || this.currentUser.EmployeeId;
      // Prevent non-supervisors from creating calendars for other employees
      if (
        targetEmployeeId !== this.currentUser.EmployeeId &&
        !this.userIsSupervisor
      ) {
        this.$message.error(
          "No tiene permiso para crear calendarios para otros empleados"
        );
        return;
      }

      await this.CreateCalendario({
        employeeID: targetEmployeeId,
        calendarioTitle: this.calendarForm.title
      });
      // Refresh calendars for the selected employee after creation
      await this.getAllCalendarsForEmployee(targetEmployeeId);
      this.closeCalendarDialog();
    },
    openShareDrawer() {
      if (this.userCanEditCalendar || this.watchingSubordinatesCalendars) {
        this.openDrawer = true;
      } else {
        this.$message.error("No tiene permiso para compartir este calendario");
      }
    },
    handleCloseDrawer() {
      this.openDrawer = false;
    },
    openCalendarDeleteDialog() {
      this.calendarDeleteDialogVisible = true;
    },
    closeCalendarDeleteDialog() {
      this.calendarDeleteDialogVisible = false;
    },
    async handleDeleteCalendar() {
      try {
        this.loading = true;
        let result = await this.DeleteCalendario({
          CalendarID: this.currentChosenCalendarID,
          EmployeeID: this.currentUser.EmployeeId
        });

        if (result.Succeded) {
          this.$message.success("Calendario eliminado exitosamente");
        } else if (result.CantRemovePersonal) {
          this.$message.error("No puede eliminar un calendario personal");
        } else if (result.BelongsToSomeoneElse) {
          this.$message.error("Este calendario fue creado por otra persona");
        }

        await this.getAllCalendarsForEmployee(this.employeeId);
        this.setPersonalCalendarAsCurrentlyChosen();
      } catch {
        this.loading = false;
        this.closeCalendarDeleteDialog();
      } finally {
        this.loading = false;
        this.closeCalendarDeleteDialog();
      }
    }
  },
  watch: {
    employeeId: async function (newEmployeeId, oldEmployeeId) {
      // When the Super User changes the employee in the dropdown, it will make an API call to return calendars belonging to the new employee
      // Prevents this from firing on the first change when the page loads. This was causing Events and Tasks to be initialized again after "Created" changed the employee ID
      // Prevents error when selection is cleared
      if (oldEmployeeId > 0 && newEmployeeId > 0) {
        // Get UserID with the EmployeeID
        const newUserApiCall = await this.$http.get(
          "/api/Employees/" + newEmployeeId
        );
        const newUserId = newUserApiCall.data.UserID;
        this.userId = newUserId;

        await this.GetPersonalCalendario(this.employeeId);
        await this.getAllCalendarsForEmployee(this.employeeId);
        await this.setPersonalCalendarAsCurrentlyChosen();
      }
    },
    currentChosenCalendarID: async function (newCalendarID) {
      if (newCalendarID) {
        await this.GetCalendario(newCalendarID);
        if (this.calendarIsPersonal) {
          // If the calendar is personal, get the Tasks of the owner of that calendar
          let calendarCreator = this.listOfCalendarsForUser.find(
            (cal) => cal.ID == newCalendarID
          ).CreatedBy;
          // await this.initTasks(calendarCreator);
          await this.initTasksGantt(calendarCreator);
        }
      }
    }
  }
};
</script>

<style>
.fc-event-resizer.fc-event-resizer-end {
  /* Disables resizing in Week and Day view */
  display: none !important;
}
.fc .fc-col-header-cell-cushion {
  color: #403d3d;
}
.fc .fc-daygrid-day-number {
  color: #403d3d;
}
.fc-daygrid-dot-event .fc-event-title {
  color: #403d3d;
}
.fc .fc-button {
  color: #403d3d !important;
  border-color: #403d3d !important;
}
.fc .fc-button-active {
  color: white !important;
}
</style>
